import * as React from "react";
import { useState, useEffect } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { toStoredThrough } from "../utils/eligibilityDisplay";
import { getEligibilityCandidates, createEligibilityPeriod } from "../services/eligibilityApi";
import EligibilityImpactAck from "./EligibilityImpactAck";

// Add Eligibility Period — create the FIRST authoritative eligibility period for a franchise that has
// no eligibility-period row (born confirmed; no proposal/review step). The franchise picker comes
// from Dash candidates (not the Fleet universe); a non-blocking readiness summary shows where the
// franchise sits in the larger onboarding process. Reuses the Option-B impact acknowledgment.
export default function EligibilityAddPeriodDialog({ open, onClose, onCreated }) {
  const [candidates, setCandidates] = useState([]);
  const [loadState, setLoadState] = useState("idle"); // idle | loading | error | loaded
  const [comCode, setComCode] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [impact, setImpact] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  // Load candidates and reset the form each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setComCode(""); setStart(""); setEnd(""); setError(null); setImpact(null); setAcknowledged(false);
    setLoadState("loading");
    let cancelled = false;
    getEligibilityCandidates()
      .then((data) => { if (!cancelled) { setCandidates(data.candidates || []); setLoadState("loaded"); } })
      .catch(() => { if (!cancelled) setLoadState("error"); });
    return () => { cancelled = true; };
  }, [open]);

  const selected = candidates.find((c) => c.com_code === comCode) || null;
  const needsAck = impact !== null && impact.count > 0;

  const resetImpact = () => { setImpact(null); setAcknowledged(false); };
  const changeFranchise = (v) => { setComCode(v); resetImpact(); };
  const changeStart = (v) => { setStart(v); resetImpact(); };
  const changeEnd = (v) => { setEnd(v); resetImpact(); };

  const handleClose = () => { if (!submitting) onClose(); };

  const handleCreate = async () => {
    setError(null);
    if (!comCode) { setError("Select a franchise."); return; }
    if (!start) { setError("Start Date is required."); return; }
    if (end && end < start) { setError("End Date must be on or after Start Date."); return; }

    setSubmitting(true);
    try {
      const result = await createEligibilityPeriod(comCode, {
        enabledFromDate: start,
        enabledThroughDate: toStoredThrough(end),
        acknowledged,
      });
      if (result.requiresAcknowledgment) {
        setImpact(result.impact);
        setSubmitting(false);
        return;
      }
      onCreated(`Eligibility period created for ${comCode}`);
    } catch (e) {
      setError(e.message || "Create failed.");
      setSubmitting(false);
    }
  };

  const readinessRow = (label, value) => (
    <>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add eligibility period</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Creates the <strong>first authoritative eligibility period</strong> for an LK/SI franchise
          that isn&rsquo;t configured yet. It is recorded as confirmed — no separate review step.
        </Typography>

        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
          These companies are detected from Dash metadata and may require review. Appearance here
          does not mean warehouse onboarding is complete.
        </Typography>

        {loadState === "error" ? (
          <Alert severity="error">Unable to load franchises. Please try again later.</Alert>
        ) : loadState !== "loaded" ? (
          <Typography variant="body2" color="text.secondary">Loading franchises…</Typography>
        ) : candidates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No franchises are awaiting a first eligibility period.
          </Typography>
        ) : (
          <>
            <TextField
              select label="Franchise" required value={comCode}
              onChange={(e) => changeFranchise(e.target.value)}
              size="small" fullWidth sx={{ mb: 1.5 }}
            >
              {candidates.map((c) => (
                <MenuItem key={c.com_code} value={c.com_code}>
                  {c.com_code}{c.franchise_name ? ` — ${c.franchise_name}` : ""}
                  {c.created_date ? ` (Dash ${c.created_date})` : ""}
                </MenuItem>
              ))}
            </TextField>

            {selected && (
              <Box
                sx={{
                  display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5, mb: 1.5,
                  p: 1, borderRadius: 1, bgcolor: "action.hover",
                }}
              >
                {readinessRow("Dash company", "✓ Exists")}
                {readinessRow(
                  "Warehouse data",
                  selected.warehouse_data_found ? "✓ Found" : "⚠ Not found"
                )}
                {readinessRow(
                  "Warehouse class seasons",
                  `${selected.season_count} season${selected.season_count === 1 ? "" : "s"}`
                )}
                {readinessRow("Eligibility", "No periods yet")}
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
              <TextField
                type="date" label="Start Date" required value={start}
                onChange={(e) => changeStart(e.target.value)}
                InputLabelProps={{ shrink: true }} size="small" fullWidth
              />
              <TextField
                type="date" label="End Date (inclusive; empty = open-ended)" value={end}
                onChange={(e) => changeEnd(e.target.value)}
                InputLabelProps={{ shrink: true }} size="small" fullWidth
              />
            </Box>

            <EligibilityImpactAck impact={impact} acknowledged={acknowledged} onChange={setAcknowledged} />
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleCreate}
          color="primary"
          variant="contained"
          disabled={submitting || loadState !== "loaded" || !comCode || !start || (needsAck && !acknowledged)}
        >
          {submitting ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
